import React, { useState, useContext, useEffect } from 'react';
import {PromptContext} from './PromptContext';
import {Box, Typography, useTheme, useMediaQuery, Card, CardContent, CardHeader, 
  Button, Grid, Badge, LinearProgress, Tabs, Tab, Divider, Paper, Avatar,
  CircularProgress, Modal, Backdrop, Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import GpsFixedIcon from '@mui/icons-material/GpsFixed'; // Target
import DescriptionIcon from '@mui/icons-material/Description'; // FileText
import AccessTimeIcon from '@mui/icons-material/AccessTime'; // Clock
import PsychologyIcon from '@mui/icons-material/Psychology'; // Brain
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MenuBookIcon from '@mui/icons-material/MenuBook'; // BookOpen
import ShowChartIcon from '@mui/icons-material/ShowChart'; // LineChart
import AddIcon from '@mui/icons-material/Add'; // Plus
import { TestAttempt, UserContext } from './UserAndProfile/UserContext';
import ScoreProgressComparison from './dashboard-components/ScoreProgressComparison';
import { ImprovementAreasContainer } from './dashboard-components/ImprovementAreasContainer';
import { SkillChartByDifficultyAndFrequency } from './dashboard-components/skills-by-difficulty-chart';
import { Link, useNavigate } from 'react-router-dom';
import { FullPracticeTestsChart } from './TestPages/FullPracticeTestsChart';
import StudyPlanWidget from './StudyPlan/StudyPlanWidget';
import CreateStudyPlanModal from './StudyPlan/CreateStudyPlanModal';
import OverallTab from './StudyPlan/OverallTab';
import { SettingsIcon } from 'lucide-react';
import { EditProfile } from './dashboard-components/EditProfile';
import { Elements } from '@stripe/react-stripe-js';
import { stripePromise } from '../utils/meta/stripePromise';
import PaymentForm from './UserSignUp/PaymentForm';
import { trackPixelEvent, PixelEvents } from '../utils/meta/metaPixel';
import ReferralCard from './dashboard-components/ReferralCard';
import StudentTrackingCard from './dashboard-components/StudentTrackingCard';
import { isFreeModeEnabled } from '../utils/accessMode';

// Custom styled components
export const StyledHeroSection = styled('section')(({ theme }) => ({
    backgroundImage: 'url(/images/picture.jpg)',
    backgroundSize: 'cover',
    backgroundPosition: 'center 145%',
    backgroundRepeat: 'no-repeat',
    backgroundAttachment: 'fixed',
    height: '32vh',
    minHeight: '20vh',
    width: '100%',
    position: 'sticky',
    flexShrink: 0,
  
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0, left: 0, right: 0, bottom: 0,
      width: '100%',
      background: 'linear-gradient(to bottom, rgba(0,0,0,0.5), rgba(0,0,0,0))',
    },
  }));

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: theme.palette.custom.lightblue,
    color: 'white',
    fontWeight: 'bold',
    padding: '0 10px',
  },
}));

const OutlinedBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: 'transparent',
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.custom.lightGrey}`,
    padding: '0 10px',
    whiteSpace: 'nowrap'
  },
}));

interface IconAvatarProps {
    bgcolor?: string;
    children?: React.ReactNode;
    sx?: any; // Allow standard sx prop to pass through
  }
  
  const IconAvatar = styled(Avatar, {
    shouldForwardProp: (prop) => prop !== 'bgcolor',
  })<IconAvatarProps>(({ theme, bgcolor }) => ({
    backgroundColor: bgcolor || `${theme.palette.primary.main}10`,
    width: 48,
    height: 48,
  }));

const FeatureCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 10,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.custom.lightGrey}`,
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
  },
}));

interface ScheduleItemProps {
    bordercolor?: string;
    children?: React.ReactNode;
    sx?: any; // Allow standard sx prop to pass through
  }
  
  const ScheduleItem = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'bordercolor',
  })<ScheduleItemProps>(({ theme, bordercolor }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    borderLeft: `4px solid ${bordercolor || theme.palette.custom.lightGrey}`,
    marginBottom: theme.spacing(1.5),
  }));

const SecondPage = () => {
  const { user, loading, error, isTrialExpired, daysLeftInTrial } = useContext(UserContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isFreeMode = isFreeModeEnabled();
  const shouldEnforcePaywall = !isFreeMode && isTrialExpired;
  const navigate = useNavigate();
  const [diagnosticTestAttemptData, setDiagnosticTestAttemptData] = useState<TestAttempt | null>(null);
  const [fullTestAttemptsData, setFullTestAttemptsData] = useState<TestAttempt[] | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const msPerDay = 1000 * 60 * 60 * 24;
  const daysUntilTest = user?.studyPlan?.testDate ? Math.ceil((new Date(user.studyPlan.testDate).getTime() - new Date().getTime()) / msPerDay) 
    : user?.testDate ? Math.ceil((new Date(user.testDate).getTime() - new Date().getTime()) / msPerDay)
      : null;

  // --- DEBUGGING LOGS ---
  //console.log("SecondPage Render - User:", user); // Log the entire user object
  //console.log("SecondPage Render - User Test Attempts:", user?.testAttempts); // Log just the attempts array

  // Find the diagnostic test attempt using its ID
  const diagnosticAttempt = user?.testAttempts?.find(attempt => {
    // --- DEBUGGING LOG INSIDE FIND ---
    console.log("Checking attempt:", attempt); // Log each attempt being checked
    return attempt.id === 'diagnostic';
  });
  const diagnosticScores = diagnosticAttempt?.diagnosticTotalScores;

  // --- DEBUGGING LOG AFTER FIND ---
  //console.log("SecondPage Render - Found Diagnostic Attempt:", diagnosticAttempt);
  //console.log("SecondPage Render - Extracted Diagnostic Scores:", diagnosticScores);

  const [isCreatePlanModalOpen, setIsCreatePlanModalOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [showUpgradeSuccess, setShowUpgradeSuccess] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentType, setPaymentType] = useState<'monthly' | 'lifetime' | null>(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  // Tab state management - default to Study Plan tab if plan exists, otherwise Invite Family tab
  const studyPlanExists = !!user?.studyPlan;
  const [activeTab, setActiveTab] = useState(studyPlanExists ? 0 : 1);

  const handleOpenCreatePlanModal = () => {
    console.log("Opening Create Study Plan Modal");
    setIsCreatePlanModalOpen(true);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleCloseCreatePlanModal = () => {
    setIsCreatePlanModalOpen(false);
  };

  const handleViewPlanClick = () => {
    navigate('/study-plan');
  };

  const handleUpgradeComplete = (data: { subscriptionId: string; customerId: string; userId: string }, paymentType: 'monthly' | 'lifetime') => {
    console.log('Upgrade completed:', data, 'Payment type:', paymentType);
    
    // Track successful payment with correct values based on payment type
    const isLifetime = paymentType === 'lifetime';
    trackPixelEvent(PixelEvents.PURCHASE, {
      content_name: isLifetime ? 'Lifetime Access' : 'Monthly Subscription',
      content_category: 'SAT Tutoring',
      currency: 'USD',
      value: isLifetime ? 120 : 39.99,
      subscription_id: data.subscriptionId,
      customer_id: data.customerId,
    });
  
    // Close modal first
    setIsUpgradeModalOpen(false);
    setShowUpgradeSuccess(true);
    
    // Hide success message after 5 seconds
    setTimeout(() => setShowUpgradeSuccess(false), 5000);
  };

  const handleFeatureClick = (e: React.MouseEvent) => {
    if (shouldEnforcePaywall) {
      e.preventDefault();
      setIsUpgradeModalOpen(true);
    }
  };

  // Load the most recent diagnostic test data --> 
  useEffect(() => {
    if (user) {
      const diagnosticTests = user.testAttempts?.filter(
        test => test.testType === 'diagnostic' && test.completed
      ) || [];
      
      if (diagnosticTests.length > 0) {
        const sortedTests = [...diagnosticTests].sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
        );
        setDiagnosticTestAttemptData(sortedTests[0]);
      } else {
         setDiagnosticTestAttemptData(null);
      }

      const fullTests = user.testAttempts?.filter(
        test => test.testType === 'full' && test.completed
      ) || [];

      if (fullTests.length > 0) {
        const sortedTests = [...fullTests].sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
        );
        setFullTestAttemptsData(sortedTests);
      } else {
        setFullTestAttemptsData([]);
      }
    } 
  }, [user]);

  if (loading) { //In full production, add a !user check here as well
    return (
      <Box sx={{display: 'flex', mt: 10, justifyContent: 'center', alignItems: 'center', minHeight: '70vh'}}>
        <CircularProgress />
        <Typography variant="body1" color="text.secondary">Loading...</Typography>
      </Box>
    );
  }
  if (error) {
    return <Box sx={{ mt: 10,p: 3 }}><Typography color="error">Error loading user data: {error.message}</Typography></Box>
  }

  // Generate header message
  const getHeaderMessage = () => {
    if (!user) return 'Welcome!';
    
    let message = `Welcome, ${user.firstName}!`;
    
    // Add SAT test countdown if available
    if (daysUntilTest && daysUntilTest > 0) {
      message += ` Your test is in ${daysUntilTest} days.`;
    }
    
    // Add trial countdown for non-premium users
    if (!isFreeMode && user.subscriptionStatus !== 'active' && daysLeftInTrial !== null && user.userRole !== 'educator') {
      if (daysLeftInTrial === 0) {
        message += ` **Your free trial has ended! Get lifetime access for just $120 (save $360!) or continue monthly for $39.99**`;
      } else if (daysLeftInTrial === 1) {
        message += ` **Last day of free trial! Lock in lifetime access for $120 (save $360!) before it expires!**`;
      } else if (daysLeftInTrial <= 3) {
        message += ` **Only ${daysLeftInTrial} days left! Secure lifetime access for $120 (save $360!) before your trial ends**`;
      } else if (daysLeftInTrial <= 7) {
        message += ` **${daysLeftInTrial} days left in trial. Get lifetime access for $120 and never pay again!**`;
      } else {
        message += ` **${daysLeftInTrial} days left in your free trial. Unlock lifetime access for just $120!**`;
      }
    }
    
    return message;
  };

  return (
    <Box sx={{ 
      mx: 'auto', 
      minHeight: '100vh'
    }}>
      {/*6/25/2025:  Show the hero section for all users including parents */}
        
        <StyledHeroSection />
        
        
        {/* Header Section - Full width with distinctive background */}
        <Box sx={{ 
            width: '100%',
            backgroundColor: theme.palette.background.paper,
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            mb: 2,
        }}>
            <Box sx={{maxWidth: '1600px', mx: 'auto', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, 
                justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2, 
                py: 2, px: "3vw"}}>
                <Box>
                    <Typography variant="h1" sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }}>
                    SAT Prep Dashboard
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}
                      dangerouslySetInnerHTML={{ __html: getHeaderMessage().replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }}
                    />
                </Box>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    {/* Show Continue My Prep button for trial users */}
                    {user && !isFreeMode && user.subscriptionStatus !== 'active' && daysLeftInTrial !== null && daysLeftInTrial >= 0 && user.userRole !== 'educator' && (
                      <Button
                        variant="contained"
                        onClick={() => {
                          // Prevent multiple clicks - double check the isProcessingPayment part here!
                          if (isProcessingPayment) return;
                          setPaymentType('lifetime');
                          setIsUpgradeModalOpen(true);
                          setShowPaymentModal(false);
                        }}
                        sx={{borderRadius: 2, whiteSpace: 'nowrap', height: '40px', minWidth: 'fit-content',
                          bgcolor: theme.palette.primary.main,
                          '&:hover': { bgcolor: theme.palette.primary.dark }
                        }}
                      >
                        {daysLeftInTrial <= 3 ? 'Get Lifetime Access - $120' : 'Unlock Lifetime Access'}
                      </Button>
                      /*Previous CTA alternatives:
                        Subscribe to Continue My Prep 
                        Continue My Prep
                        Unlock Full Access
                        Continue My SAT Journey
                        Get Lifetime Access
                        */
                    )}
                    
                    {/* Edit Profile Button */}
                    {user ? (
                      <Button
                        variant="outlined"
                        startIcon={<SettingsIcon />}
                        onClick={() => setIsEditing(true)}
                        sx={{borderRadius: 2, whiteSpace: 'nowrap', height: '40px', minWidth: 'fit-content'}}
                      >
                        Edit Profile
                      </Button>
                    ) : (
                        <Button variant="contained" startIcon={<SettingsIcon />} component={Link} to="/create-profile"
                        sx={{borderRadius: 2, whiteSpace: 'nowrap', height: '40px', minWidth: 'fit-content'}}
                        >
                          Create Profile
                        </Button>
                    )}
                </Box>
            </Box>
            <EditProfile open={isEditing} onClose={() => setIsEditing(false)} />
        </Box>

        {/* Content container with max width */}
        <Box sx={{
            maxWidth: '1600px',
            mx: 'auto',
            px: { xs: 1, sm: 2, md: 4 },
            position: 'relative'
        }}>

        {/* Soft Paywall Overlay */}
        {shouldEnforcePaywall && (
          <Box sx={{position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
              zIndex: 10,
              display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
              backdropFilter: 'blur(2px)',
              cursor: 'pointer',
              pt: 8
            }}
            onClick={() => setIsUpgradeModalOpen(true)}
          >
            <Card sx={{ 
              p: 4, 
              textAlign: 'center', 
              maxWidth: 400,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
              borderRadius: 3
            }}>
              <Typography variant="h5" gutterBottom>
                Keep pursuing your SAT goals!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Continue your SAT journey to earn $20,000+ in scholarships and admissions to your dream colleges by having full access to all features!
              </Typography>
              <Button 
                variant="contained" 
                size="large"
                onClick={() => setIsUpgradeModalOpen(true)}
              >
                Continue My Prep
              </Button>
            </Card>
          </Box>
        )}

        {/* Main Content - slightly dimmed if trial expired */}
        <Box sx={{ opacity: shouldEnforcePaywall ? 0.3 : 1, pointerEvents: shouldEnforcePaywall ? 'none' : 'auto' }}>
          {/* Features and Performance Section */}
          <Box sx={{display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, // 2/3 - 1/3 split on medium screens and up
            gap: 3, mb: 2, width: '100%'
          }}>
            {/* Conditional Content Based on User Role 
            {user?.userRole === 'parent' ? (
              <>
                {/* Student Tracking - Takes 2/3 of the space - COMMENTED OUT FOR NOW */}
                {/* <StudentTrackingCard /> */}

                {/* Placeholder for parents when StudentTrackingCard is disabled 
                <Card elevation={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
                  <Typography variant="h6" color="text.secondary">
                    Student tracking features coming soon!
                  </Typography>
                </Card>

                {/* Referral Card - Takes 1/3 of the space - MOVED TO SKILLS SECTION */}
                {/* <ReferralCard /> */}
              {/*</>
            ) : (
              <>*/}
            {user?.userRole === 'parent' ? (
                <StudentTrackingCard />
              ) : (
                <Card elevation={1}>
                  <CardHeader title="SAT Features" subheader="Choose what you want to work on today" sx={{px: {xs: 1, sm: 2}, pt: 2, pb: 0}}/>
                  <CardContent sx={{px: {xs: 0.5, sm: 2}}}>
                    <Box sx={{display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }}}>
                      {/* Diagnostic Test */}
                      <FeatureCard elevation={0} onClick={handleFeatureClick}>
                        {/* Combine Icon, Badge, and Score Display */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <IconAvatar bgcolor={`${theme.palette.primary.main}10`}>
                              <GpsFixedIcon sx={{ color: theme.palette.primary.main }} />
                            </IconAvatar>
                            <Typography variant="h5" component="h3" sx={{ fontSize: {xs: theme.typography.h6.fontSize, sm: theme.typography.h5.fontSize} }}>Diagnostic Test</Typography>
                            {diagnosticTestAttemptData && (
                              <>
                              <Typography variant={isMobile ? "h5" : isTablet ? "h4" : "h3"} component="span" fontWeight="bold" color={theme.palette.text.primary}>
                                {/* Display the score range */}
                                {diagnosticTestAttemptData?.totalScore}
                              </Typography>
                              {!isTablet && (
                                <Typography variant="body2" component="span" color="text.secondary" sx={{ ml: 0.5 }}>
                                / 1600
                              </Typography>
                              )}
                              
                              {/*<Button variant="outlined" sx={{ p: 1, display: 'block', lineHeight: 1.2 }} // Adjust styling for link appearance
                                component={Link} // Make it a link if needed
                                to="/diagnostic-test-results" // Add appropriate link destination
                              >
                                View Results
                              </Button>*/}
                            </>
                            )}
                              {/*Formerly had this as an alternative if there was no diagnostic test attempt data:
                              <Typography variant="body2" component="span" color="text.secondary" fontWeight="bold">
                                We recommend starting here!
                              </Typography>*/}
                            

                          </Box>
                          {!diagnosticTestAttemptData && (
                            <StyledBadge badgeContent="Recommended" sx={{ transform: 'translateX(-48px)' }}/>
                          )}
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                          Take a 20-minute test to identify your strengths and weaknesses.
                        </Typography>
                        {diagnosticTestAttemptData ? (
                          <Button component={Link} to="/diagnostic-test-results" variant="contained" 
                            sx={{ p: 1, display: 'block', lineHeight: 1.2, 
                            bgcolor: theme.palette.primary.main,
                            '&:hover': { bgcolor: theme.palette.primary.dark }}} 
                          
                          >
                            View Diagnostic Test Results
                          </Button>

                        ) : (
                          <Button component={Link} to="/diagnostic-test" variant="contained" fullWidth
                            sx={{marginTop: 'auto',
                              bgcolor: theme.palette.secondary.main,
                              '&:hover': { bgcolor: theme.palette.secondary.dark }
                            }}>
                              Start Diagnostic Test
                          </Button>
                        )}
                        
                      </FeatureCard>

                      {/* Skill Practice */}
                      <FeatureCard elevation={0} onClick={handleFeatureClick}>
                        <Box sx={{ display: 'flex', position: 'relative', justifyContent: 'flex-start', alignItems: 'center', gap: 2, mb: 1.5 }}>
                          <IconAvatar bgcolor={`${theme.palette.custom.red}10`}>
                            <MenuBookIcon sx={{ color: theme.palette.custom.red }} />
                          </IconAvatar>
                          <Typography variant="h5" component="h3" sx={{ fontSize: {xs: theme.typography.h6.fontSize, sm: theme.typography.h5.fontSize} }}>Skill Practice</Typography>
                          {diagnosticTestAttemptData && (
                            <StyledBadge 
                              badgeContent="Recommended" 
                              sx={{ 
                                position: 'absolute',
                                top: '4px',
                                right: '48px',
                                zIndex: 1
                              }}
                            />
                          )}
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                          Focus on specific areas that need improvement.
                        </Typography>
                        <Button variant="contained" fullWidth component={Link} to="/skill-practice" 
                          sx={{
                            marginTop: 'auto',
                            bgcolor: diagnosticTestAttemptData ? theme.palette.secondary.main : theme.palette.primary.main,
                            '&:hover': { 
                              bgcolor: diagnosticTestAttemptData ? theme.palette.secondary.dark : theme.palette.primary.dark 
                            }
                          }}
                        >
                          Start Skill Practice
                        </Button>
                      </FeatureCard>
                      
                      {/* Full-Length Tests */}
                      <FeatureCard elevation={0} onClick={handleFeatureClick}>
                         {/* Combine Icon, Badge, and Test Count Display */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <IconAvatar bgcolor={`${theme.palette.secondary.main}10`}>
                                <DescriptionIcon sx={{ color: theme.palette.secondary.main }} />
                              </IconAvatar>
                              <Typography variant="h5" component="h3" sx={{ fontSize: {xs: theme.typography.h6.fontSize, sm: theme.typography.h5.fontSize} }}>Full-Length Tests</Typography>
                              {fullTestAttemptsData && fullTestAttemptsData.length > 0 && (
                              <Box>
                                <Typography variant={isTablet ? "h4" : "h3"} component="span" fontWeight="bold" color={theme.palette.text.primary}>
                                  {fullTestAttemptsData.length}</Typography>
                                <Typography variant="body2" component="span"> completed</Typography>
                                {/*<Typography variant="body2" color="text.secondary" display="block">6 available</Typography>*/}
                              </Box>
                              )}
                            </Box>
                          {/*<OutlinedBadge badgeContent="8 Available" /> Keep or adjust badge as needed */}
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                          Full SAT tests under timed conditions but can be paused and resumed.
                        </Typography>
                        <Button component={Link} to="/full-test" variant="contained" fullWidth 
                          sx={{bgcolor: theme.palette.primary.main, marginTop: 'auto',
                              '&:hover': { bgcolor: theme.palette.primary.dark }
                        }}>
                          Take Practice Test
                        </Button>
                      </FeatureCard>
                      
                      {/* Review Missed Questions */}
                      <FeatureCard elevation={0} onClick={handleFeatureClick}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 2, mb: 1.5 }}>
                          <IconAvatar bgcolor={`${theme.palette.custom.green}10`}>
                            <CheckCircleIcon sx={{ color: theme.palette.custom.green }} />
                          </IconAvatar>
                          <Typography variant="h5" component="h3" sx={{ fontSize: {xs: theme.typography.h6.fontSize, sm: theme.typography.h5.fontSize} }}>Review Missed Questions</Typography>
                          {/*<OutlinedBadge badgeContent="14 Questions" sx={{ transform: 'translateX(-45px)' }} />*/}
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                          Learn from your mistakes with detailed explanations.
                        </Typography>
                        <Button variant="contained" component={Link} to="/review-missed-questions" fullWidth
                          sx={{marginTop: 'auto'}}>Start Review</Button>
                      </FeatureCard>
                      
                      
                    </Box>
                  </CardContent>
                </Card>
              )}

                {/* Study Plan Widget for students / Referral for parents - Takes 1/3 of the space */}
                {/* TODO: Consider adding tabs or minimizing functionality later for better UX */}
                <Card elevation={1} sx={{
                  height: '100%',
                  maxHeight: '600px',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  {/* Tabs Header */}
                  <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth" sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tab sx={{px: {xs: 1, sm: 2}}} label={
                      user?.userRole === 'parent' ? "Invite Student" : (studyPlanExists ? "Study Plan" : "Create Study Plan")
                    } />
                    <Tab sx={{px: {xs: 1, sm: 2}}} label="Invite Family & Friends"/>
                  </Tabs>
                  
                  {/* Tab Content Area */}
                  <Box sx={{ 
                    flex: 1,
                    overflow: 'auto',
                    p: 1,
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: '#f1f1f1',
                      borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#c1c1c1',
                      borderRadius: '3px',
                      '&:hover': {
                        background: '#a8a8a8',
                      },
                    },
                  }}>
                    {activeTab === 0 && (
                      user?.userRole === 'parent' ? (
                        <ReferralCard embedded={true} />
                      ) : (
                        <StudyPlanWidget 
                          user={user}
                          onCreatePlanClick={handleOpenCreatePlanModal}
                          onViewPlanClick={handleViewPlanClick}
                          embedded={true}
                        />
                      )
                    )}
                    {activeTab === 1 && (
                      <ReferralCard embedded={true} />
                    )}
                  </Box>
                </Card>

                {/* Alternative: Study Plan Widget for Students / Referral for Parents/Educators - Takes 1/3 of the space 
                {user?.userRole === 'parent' || user?.userRole === 'educator' ? (
                  <ReferralCard />
                ) : (
                  <StudyPlanWidget user={user}
                    onCreatePlanClick={handleOpenCreatePlanModal}
                    onViewPlanClick={handleViewPlanClick}/>
                )}
                */}
              {/*</>
            )}*/}
          </Box>

        {/* Schedule and Progress Section 
        <Grid container spacing={3}>
            {/* Study Schedule 
            <Grid sx={{gridColumn: { xs: "span 12", md: "span 6" } }}>
            <Card elevation={1}>
                <CardHeader 
                title="Study Schedule" 
                subheader="This week's plan"
                />
                <CardContent>
                {/* Completed - Reading Practice 
                <ScheduleItem bordercolor={theme.palette.custom.green}>
                    <IconAvatar sx={{ width: 40, height: 40 }} bgcolor={`${theme.palette.custom.green}10`}>
                    <CheckCircleIcon sx={{ color: theme.palette.custom.green }} />
                    </IconAvatar>
                    <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight="medium">Reading Practice</Typography>
                    <Typography variant="body2" color="text.secondary">Monday • Completed</Typography>
                    </Box>
                    <Typography variant="body2">45 min</Typography>
                </ScheduleItem>

                  {/* Completed - Math Drills 
                  <ScheduleItem bordercolor={theme.palette.custom.green}>
                      <IconAvatar sx={{ width: 40, height: 40 }} bgcolor={`${theme.palette.custom.green}10`}>
                      <CheckCircleIcon sx={{ color: theme.palette.custom.green }} />
                      </IconAvatar>
                      <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight="medium">Math Drills</Typography>
                      <Typography variant="body2" color="text.secondary">Tuesday • Completed</Typography>
                      </Box>
                      <Typography variant="body2">30 min</Typography>
                  </ScheduleItem>

                  {/* In Progress - Writing Section 
                  <ScheduleItem bordercolor={theme.palette.primary.main}>
                      <IconAvatar sx={{ width: 40, height: 40 }} bgcolor={`${theme.palette.primary.main}10`}>
                      <CalendarTodayIcon sx={{ color: theme.palette.primary.main }} />
                      </IconAvatar>
                      <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight="medium">Writing Section</Typography>
                      <Typography variant="body2" color="text.secondary">Today • In Progress</Typography>
                      </Box>
                      <Typography variant="body2">60 min</Typography>
                  </ScheduleItem>

                  {/* Upcoming - Practice Test 
                  <ScheduleItem bordercolor={theme.palette.custom.lightGrey}>
                      <IconAvatar sx={{ width: 40, height: 40 }} bgcolor={theme.palette.custom.lightGrey}>
                      <DescriptionIcon sx={{ color: theme.palette.custom.darkGrey }} />
                      </IconAvatar>
                      <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight="medium">Practice Test #3</Typography>
                      <Typography variant="body2" color="text.secondary">Saturday • Upcoming</Typography>
                      </Box>
                      <Typography variant="body2">3 hrs</Typography>
                  </ScheduleItem>

                  <Button 
                      variant="outlined" 
                      fullWidth 
                      startIcon={<AddIcon />}
                      sx={{ mt: 2 }}
                  >
                      Add Study Session
                  </Button>
                  </CardContent>
              </Card>
              </Grid>

              


          </Grid>*/}
              {/* Study Plan Section 
              <OverallTab />*/}
              
              {/* Score Assessment Section */}
              {/* Main container for the top section */}
              <Box sx={{display: 'flex', flexDirection: 'row', gap: 4,mb: 4, width: '100%' }}>
                {/*Old formatting for when the chart is in: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, // 2/3 - 1/3 split on medium screens and up
                gap: 3,  */}
                {/*<ScoreProgressComparison />*/}
                {/* Right Section: Chart 
                Put this back in once the Full Practice Tests are implemented!!
                <FullPracticeTestsChart />*/}
              </Box>
              
              {/* Improvement Areas Section */}
              <Box sx={{ mt: 4 }}>
              <Typography variant="h5" gutterBottom sx={{textAlign: 'center'}}>Areas to Improve</Typography>
                  <ImprovementAreasContainer />
              </Box>
              
              
              <Box sx={{ mt: 4, mb: 4 }}>
                {/* Skills Performance Chart - COMMENTED OUT FOR NOW
                {user?.userRole === 'parent' || user?.userRole === 'educator' ? (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3 }}>
                    {/* Skills Chart - Takes 2/3 of the space 
                    <Box>
                      <SkillChartByDifficultyAndFrequency />
                    </Box>
                    
                    {/* Referral Card - Takes 1/3 of the space 
                    <ReferralCard />
                  </Box>
                ) : (
                  <SkillChartByDifficultyAndFrequency />
                )*/}
                <SkillChartByDifficultyAndFrequency />
              </Box>

          
        </Box>
        <CreateStudyPlanModal
        open={isCreatePlanModalOpen}
        onClose={handleCloseCreatePlanModal}
      />
        </Box>

        {/* Lifetime Access Upgrade Modal */}
        <Modal
          open={isUpgradeModalOpen}
          onClose={() => setIsUpgradeModalOpen(false)}
          closeAfterTransition
          BackdropComponent={Backdrop}
          BackdropProps={{ timeout: 500 }}
        >
          <Box sx={{position: 'absolute', top: '50%', left: '50%',transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: '500px' }, maxHeight: '90vh', overflow: 'auto',
            bgcolor: 'background.paper',
            borderRadius: 2,boxShadow: 24,
            p: { xs: 2, sm: 3 }
          }}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.4rem', sm: '1.8rem' } }}>
                Choose Your Plan
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
                Unlock your full SAT potential
              </Typography>
            </Box>
            
            {/* Lifetime Option - Highlighted */}
            <Card sx={{ 
              mb: 2, 
              border: '3px solid', 
              borderColor: 'primary.main', 
              position: 'relative',
              bgcolor: 'primary.light',
              color: 'primary.contrastText'
            }}>
              <Box sx={{ 
                position: 'absolute', 
                top: +5, 
                left: '50%', 
                transform: 'translateX(-50%)', 
                bgcolor: 'secondary.main', 
                color: 'white', 
                px: { xs: 1.5, sm: 2 }, 
                py: 0.5, 
                borderRadius: 1, 
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                fontWeight: 'bold',
                whiteSpace: 'nowrap'
              }}>
                🎉 BEST VALUE - Save $360!
              </Box>
              <CardContent sx={{ textAlign: 'center', pt: { xs: 4.5, sm: 5 }, pb: { xs: 1.5, sm: 2 } }}>
                <Typography variant="h2" sx={{ 
                  fontSize: { xs: '2.2rem', sm: '2.7rem' }, 
                  fontWeight: 'bold',
                  color: 'white',
                  mb: 0.5
                }}>
                  $120
                </Typography>
                {/*<Typography variant="body1" sx={{ 
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  color: 'rgba(255,255,255,0.9)',
                  mb: 1
                }}>
                  One-time payment
                </Typography>*/}
                <Typography variant="h5" sx={{ 
                  fontSize: { xs: '1rem', sm: '1.2rem' },
                  fontWeight: 'bold',
                  color: 'white',
                  mb: 1
                }}>
                  Lifetime Access
                </Typography>
                <Typography variant="body2" sx={{ 
                  fontSize: { xs: '0.8rem', sm: '0.85rem' },
                  color: 'rgba(255,255,255,0.8)',
                  mb: 1.5,
                  lineHeight: 1.3
                }}>
                  • Personalized AI tutoring <br/>• All practice tests <br/>• Study plans & tracking <br/>• All future features
                </Typography>
                <Button 
                  variant="contained" 
                  fullWidth 
                  size="large"
                  sx={{ 
                    py: { xs: 1.2, sm: 1.5 },
                    fontSize: { xs: '0.85rem', sm: '0.95rem' },
                    fontWeight: 'bold',
                    bgcolor: 'white',
                    color: 'primary.main',
                    '&:hover': {
                      bgcolor: 'grey.100'
                    }
                  }}
                  onClick={() => {
                    // Prevent multiple clicks
                    if (isProcessingPayment) return;
                    setPaymentType('lifetime');
                    setIsUpgradeModalOpen(false);
                    setShowPaymentModal(true);
                  }}
                >
                  Get Lifetime Access - $120
                </Button>
              </CardContent>
            </Card>

            {/* Monthly Option */}
            <Card sx={{ mb: 1.5, border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ textAlign: 'center', py: { xs: 1.5, sm: 2 } }}>
                <Typography variant="h3" sx={{ 
                  fontSize: { xs: '1.8rem', sm: '2.2rem' },
                  fontWeight: 'bold',
                  mb: 0.5
                }}>
                  $39.99
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  per month
                </Typography>
                <Typography variant="h6" sx={{ 
                  fontSize: { xs: '0.95rem', sm: '1rem' },
                  mb: 1
                }}>
                  Monthly Subscription
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ 
                  fontSize: { xs: '0.85rem', sm: '0.9rem' },
                  mb: 2
                }}>
                  Cancel anytime
                </Typography>
                <Button 
                  variant="outlined" 
                  fullWidth 
                  sx={{ 
                    py: { xs: 1.2, sm: 1.5 },
                    fontSize: { xs: '0.85rem', sm: '0.95rem' }
                  }}
                  onClick={() => {
                    // Prevent multiple clicks
                    if (isProcessingPayment) return;
                    setIsUpgradeModalOpen(false);
                    setPaymentType('monthly');
                    setShowPaymentModal(true);
                  }}
                >
                  Start Monthly - $39.99/mo
                </Button>
              </CardContent>
            </Card>

            {/* Close button */}
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Button 
                variant="text" 
                onClick={() => setIsUpgradeModalOpen(false)}
                sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' } }}
              >
                Sign-up Later
              </Button>
            </Box>
          </Box>
        </Modal>

        {/* Monthly Payment Modal */}
        <Modal
          open={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setPaymentType(null);
            setIsProcessingPayment(false);
          }}
          closeAfterTransition
          BackdropComponent={Backdrop}
          BackdropProps={{ timeout: 500 }}
        >
          <Box sx={{position: 'absolute', top: '50%', left: '50%',transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: '500px' }, maxHeight: '90vh', overflow: 'auto',
            bgcolor: 'background.paper',
            borderRadius: 2,boxShadow: 24,
            p: 0
          }}>
            {user && paymentType && (
              <Elements stripe={stripePromise}>
                <PaymentForm //Does this handle logic of monthly vs. lifetime?
                  //This comes from the 2b-PaymentForm.tsx file.
                  user={{
                    uid: user.uid,
                    email: user.email,
                    firstName: user.firstName,
                    lastName: user.lastName
                  }}
                  paymentType={paymentType}
                  onComplete={(data) => {
                    handleUpgradeComplete(data, paymentType!);
                    setShowPaymentModal(false);
                    setPaymentType(null);
                    setIsProcessingPayment(false);
                  }}
                />
              </Elements>
            )}
          </Box>
        </Modal>

        {showUpgradeSuccess && (
          <Alert severity="success" sx={{ mb: 2, position: 'fixed', top: 100, right: 20, zIndex: 9999 }}>
            🎉 Welcome to full access! Your subscription is now active.
          </Alert>
        )}

        {/* TODO (Phase 3): Add AI Voice Coach component here
            - Floating action button in bottom-right corner with animated character icon
            - Green circle indicator (like social media) showing it's available to talk
            - Expands to modal overlay when clicked
            - Voice + text input for natural conversation
            - Should be on EVERY page, not just home dashboard
            - Context: user's progress, study plan, recent activity
            - Use cases: "What should I study?", "How am I doing?", "Explain my plan"
            - Component: <AIVoiceCoach />
            - See STUDY_PLAN_REDESIGN.md Phase 3 for full details
        */}
    </Box>

    
  );
};

export default SecondPage;


{/* Progress Tracking 
            <Grid sx={{gridColumn: { xs: "span 12", md: "span 6" } }}>
            <Card elevation={1}>
                <CardHeader 
                title={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <Box>
                        <Typography variant="h6" component="div">Progress Tracking</Typography>
                        <Typography variant="body2" color="text.secondary">Score improvement over time</Typography>
                    </Box>
                    <Tabs 
                        value={tabValue} 
                        onChange={handleTabChange}
                        sx={{
                        minHeight: 'auto',
                        '& .MuiTabs-indicator': {
                            backgroundColor: theme.palette.primary.main,
                        },
                        }}
                    >
                        <Tab 
                        label="Score" 
                        sx={{ 
                            minHeight: 'auto',
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            py: 1,
                        }} 
                        />
                        <Tab 
                        label="Skills" 
                        sx={{ 
                            minHeight: 'auto',
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            py: 1,
                        }} 
                        />
                    </Tabs>
                    </Box>
                }
                sx={{ pb: 0 }}
                />
                <CardContent>
                {/* Chart Placeholder 
                <Box sx={{ 
                    height: 250, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    bgcolor: theme.palette.background.paper,
                    borderRadius: 1,
                    mb: 3
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ShowChartIcon sx={{ color: theme.palette.primary.main }} />
                    <Typography color="text.secondary">
                        Chart showing score improvement from 1180 to 1320
                    </Typography>
                    </Box>
                </Box>

                {/* Stats Summary 
                <Grid container spacing={2}>
                    <Grid sx={{gridColumn: { xs: "span 6", md: "span 3" } }}>
                    <Box sx={{ p: 2, bgcolor: theme.palette.background.paper, borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">Starting Score</Typography>
                        <Typography variant="h5" fontWeight="bold">1180</Typography>
                    </Box>
                    </Grid>
                    <Grid sx={{gridColumn: { xs: "span 6", md: "span 3" } }}>
                    <Box sx={{ p: 2, bgcolor: theme.palette.background.paper, borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">Current Score</Typography>
                        <Typography variant="h5" fontWeight="bold">1320</Typography>
                    </Box>
                    </Grid>
                    <Grid sx={{gridColumn: { xs: "span 6", md: "span 3" } }}>
                    <Box sx={{ p: 2, bgcolor: theme.palette.background.paper, borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">Improvement</Typography>
                        <Typography variant="h5" fontWeight="bold" sx={{ color: theme.palette.custom.green }}>+140</Typography>
                    </Box>
                    </Grid>
                    <Grid sx={{gridColumn: { xs: "span 6", md: "span 3" } }}>
                    <Box sx={{ p: 2, bgcolor: theme.palette.background.paper, borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">Target Score</Typography>
                        <Typography variant="h5" fontWeight="bold">1450</Typography>
                    </Box>
                    </Grid>
                </Grid>
                </CardContent>
            </Card>
            </Grid>*/}

/*
Remnants from when I used to have ChatComponent on the right side of the screen. Probably delete after the overhaul.
{/*<StyledSplitContainer 
                            isChatVisible={isChatVisible}
                            sizes={isChatVisible ? [100 - chatSize, chatSize] : [100, 0]} //initial sizes but changeable
                                minSize={[45, 0]} //Minimum 45% for questions, no minimum for chat
                                maxSize={[window.innerWidth * 0.75, window.innerWidth * 0.50]} //Maximum 85% for questions, 55% for chat
                                gutterSize={isChatVisible ? 10 : 0} // Size of the divider total width, not line itself
                                //snapOffset={0} // Snap to 10px from the edge
                                dragInterval={1} // Snap to 1% increments
                                onDrag={(sizes) => {setChatSize(sizes[1]);}}
                        >
                            <Box sx={{ position: 'relative', height: '100%', overflow: 'auto'}}>
                                <DiagnosticTest />
                            </Box>
                            
                            <Box sx={{position: 'relative', height: '100%', minWidth: '30px', 
                            display: 'flex', flexDirection: 'column', justifyContent: 'flex-start',
                            }}
                            >
                                {/*{isChatVisible && <ChatComponent />}
                                <Tooltip title={isChatVisible ? "Hide AI Chat" : "Show AI Chat"}>
                                    <ToggleButton sx={{top: '6vh', right: '.25vw' }} onClick={toggleChat}>
                                        {isChatVisible ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                                    </ToggleButton>
                                </Tooltip>
                            </Box>
                        </StyledSplitContainer>}



<Grid container spacing={3} sx={{ flex: 1 }}>
                    {/* Left section - Skills and Questions }
                    {/*size means how many columns (out of 12) to take up. XS is 1 column. }
                    <Grid component="div" size={{xs: 12, md: 8, lg: 9}}>
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, height: '100%'}}>  
                            <ButtonChoice />
                            <Box sx={{flex: 1, borderRadius: 1, p: 2, boxShadow: 1}}>
                            
                                {/* Question display area }
                                <ProgressBar />
                                {/* Question content will go here }
                            </Box>
                        </Box>
                    </Grid>

                    {/* Right section - Chat }
                    <Grid component="div" size={{xs: 12, md: 4, lg: 3}}>
                        <Box sx={{height: '100%', bgcolor: 'background.paper', borderRadius: 1, p: 2, boxShadow: 1}}>
                        <ChatComponent />
                        </Box>
                    </Grid>
                </Grid>
*/



/*
      <section className = "hero-second-page">
                        
                </section>


In place of the current <Grid container spaceing...> </Grid>
            <div className="sat-area">
                    <div className="skills-and-questions-left-section">
                        <ButtonChoice />
                    </div>
                    <div className="chat-component-right-section">
                        <ChatComponent />
                    </div>
                </div>
                <ProgressBar />


      */

